/**
 * Guided photo capture: one tile per slot, opening the camera on phones.
 * Photos are resized on the device as soon as they're picked, so the upload
 * after saving is the small version.
 */
import { useRef, useState } from "react";
import { PHOTO_SLOTS, type SlotKey } from "@sable/domain";
import { t, tk } from "../i18n/index.ts";
import { preparePhoto, type PreparedPhoto } from "../lib/images.ts";
import { IconCamera, IconPlus, IconX } from "./icons.tsx";

export interface PendingPhoto {
  key: string;
  slot: SlotKey;
  photo: PreparedPhoto;
}

function SlotTile({ slot, required, photo, onPick, onRemove }: {
  slot: SlotKey;
  required: boolean;
  photo?: PendingPhoto;
  onPick: (file: File) => void;
  onRemove?: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  if (photo) {
    return (
      <div className="slot filled">
        <img src={photo.photo.previewUrl} alt={tk("slot", slot)} />
        <span className="slot-label">{tk("slot", slot)}</span>
        {onRemove && <button type="button" className="slot-remove" onClick={onRemove} aria-label={t("item.removePhoto")}><IconX size={14} /></button>}
      </div>
    );
  }
  return (
    <button type="button" className={`slot${required ? " required" : ""}`} onClick={() => input.current?.click()}>
      {slot === "extra" ? <IconPlus /> : <IconCamera />}
      {slot === "extra" ? t("intake.addPhoto") : tk("slot", slot)}
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onPick(file);
        }}
      />
    </button>
  );
}

export function PhotoSlots({ photos, onChange }: { photos: PendingPhoto[]; onChange: (next: PendingPhoto[]) => void }) {
  const [busy, setBusy] = useState(0);
  const [error, setError] = useState<string | null>(null);

  async function add(slot: SlotKey, file: File) {
    setBusy((n) => n + 1);
    setError(null);
    try {
      const photo = await preparePhoto(file);
      onChange([...photos.filter((p) => slot === "extra" || p.slot !== slot), { key: crypto.randomUUID(), slot, photo }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy((n) => n - 1);
    }
  }

  function remove(key: string) {
    const gone = photos.find((p) => p.key === key);
    if (gone) URL.revokeObjectURL(gone.photo.previewUrl);
    onChange(photos.filter((p) => p.key !== key));
  }

  const extras = photos.filter((p) => p.slot === "extra");
  return (
    <div className="stack">
      <div className="slots">
        {PHOTO_SLOTS.map((s) => {
          const photo = photos.find((p) => p.slot === s.slot);
          return (
            <SlotTile key={s.slot} slot={s.slot} required={s.required} photo={photo}
              onPick={(f) => void add(s.slot, f)} onRemove={photo ? () => remove(photo.key) : undefined} />
          );
        })}
        {extras.map((p) => (
          <SlotTile key={p.key} slot="extra" required={false} photo={p} onPick={() => undefined} onRemove={() => remove(p.key)} />
        ))}
        <SlotTile slot="extra" required={false} onPick={(f) => void add("extra", f)} />
      </div>
      {busy > 0 && <p className="muted small">{t("app.loading")}</p>}
      {error && <p className="form-error">{error}</p>}
    </div>
  );
}
